import { useCallback, useEffect, useMemo, useState } from 'react';
import { knowledgeAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Folder, FileText, Loader2 } from 'lucide-react';

const CHECK = { ON: 'on', OFF: 'off', PARTIAL: 'partial' };

/** Estado efectivo del nodo (incluye PARTIAL derivado de hijos). */
function getCheckState(node, checkedMap) {
  const key = node.path || '';
  const children = node.children || [];
  if (!children.length) {
    return checkedMap[key] ?? CHECK.ON;
  }
  const childStates = children.map((c) => getCheckState(c, checkedMap));
  const allOn = childStates.every((s) => s === CHECK.ON);
  const allOff = childStates.every((s) => s === CHECK.OFF);
  const explicit = checkedMap[key];
  if (allOn) {
    return explicit === CHECK.OFF ? CHECK.PARTIAL : CHECK.ON;
  }
  if (allOff) {
    return explicit === CHECK.ON ? CHECK.PARTIAL : CHECK.OFF;
  }
  return CHECK.PARTIAL;
}

function computeExcludePaths(root, checkedMap) {
  const excludes = [];

  function walk(node) {
    if (!node) return;
    const key = node.path || '';
    const state = getCheckState(node, checkedMap);
    const children = node.children || [];

    if (state === CHECK.OFF) {
      if (key) {
        excludes.push(key);
        return;
      }
      // Raiz desmarcada: excluir cada carpeta de primer nivel (path vacio no persiste en backend)
      children.forEach((child) => {
        if (getCheckState(child, checkedMap) === CHECK.OFF && child.path) {
          excludes.push(child.path);
        } else {
          walk(child);
        }
      });
      return;
    }

    if (state === CHECK.PARTIAL || state === CHECK.ON) {
      children.forEach(walk);
    }
  }

  walk(root);
  return excludes.filter((p) => p !== undefined && p !== '');
}

function initCheckedFromTree(root) {
  const map = {};
  const walk = (node) => {
    if (!node) return;
    const key = node.path || '';
    map[key] = node.excluded ? CHECK.OFF : CHECK.ON;
    (node.children || []).forEach(walk);
  };
  walk(root);
  return map;
}

function toggleNode(node, checkedMap, targetState) {
  const next = { ...checkedMap };
  const apply = (n, state) => {
    next[n.path || ''] = state;
    (n.children || []).forEach((c) => apply(c, state));
  };
  apply(node, targetState);
  return next;
}

function TreeNode({ node, depth, checkedMap, expanded, onToggleExpand, onToggleCheck }) {
  const isDir = node.type === 'dir';
  const state = getCheckState(node, checkedMap);
  const isExpanded = expanded[node.path || ''] ?? depth < 1;

  const handleCheck = () => {
    const state = getCheckState(node, checkedMap);
    const next = state === CHECK.ON ? CHECK.OFF : CHECK.ON;
    onToggleCheck(node, next);
  };

  return (
    <div>
      <div
        className="flex items-center gap-1 py-0.5 hover:bg-zinc-50 rounded-sm"
        style={{ paddingLeft: `${depth * 14}px` }}
      >
        {isDir ? (
          <button
            type="button"
            className="p-0.5 text-zinc-500"
            onClick={() => onToggleExpand(node.path || '')}
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <input
          type="checkbox"
          className="rounded"
          checked={state === CHECK.ON}
          ref={(el) => {
            if (el) el.indeterminate = state === CHECK.PARTIAL;
          }}
          onChange={handleCheck}
        />
        {isDir ? (
          <Folder className="w-3 h-3 text-amber-600 flex-shrink-0" />
        ) : (
          <FileText className={`w-3 h-3 flex-shrink-0 ${node.indexable ? 'text-blue-500' : 'text-zinc-300'}`} />
        )}
        <span className={`text-[11px] truncate ${node.indexable === false && !isDir ? 'text-zinc-400' : ''}`}>
          {node.name}
          {!isDir && node.indexable === false && (
            <span className="text-zinc-400 ml-1">(no indexable)</span>
          )}
        </span>
      </div>
      {isDir && isExpanded && (node.children || []).map((child) => (
        <TreeNode
          key={child.path || child.name}
          node={child}
          depth={depth + 1}
          checkedMap={checkedMap}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
          onToggleCheck={onToggleCheck}
        />
      ))}
    </div>
  );
}

export default function KBRepoTreeDialog({ repoId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [checkedMap, setCheckedMap] = useState({});
  const [expanded, setExpanded] = useState({});

  const loadTree = useCallback(async () => {
    if (!repoId) return;
    setLoading(true);
    try {
      const res = await knowledgeAPI.getRepoTree(repoId);
      setTreeData(res.data);
      setCheckedMap(initCheckedFromTree(res.data?.root));
      setExpanded({ '': true });
    } catch (e) {
      toast.error(formatApiError(e));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [repoId, onOpenChange]);

  useEffect(() => {
    if (open && repoId) loadTree();
  }, [open, repoId, loadTree]);

  const handleToggleCheck = (node, targetState) => {
    setCheckedMap((prev) => toggleNode(node, prev, targetState));
  };

  const handleToggleExpand = (path) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleSelectAll = () => {
    if (!treeData?.root) return;
    setCheckedMap(toggleNode(treeData.root, {}, CHECK.ON));
  };

  const handleDeselectAll = () => {
    if (!treeData?.root) return;
    setCheckedMap(toggleNode(treeData.root, {}, CHECK.OFF));
  };

  const handleSave = async () => {
    if (!treeData?.root) return;
    setSaving(true);
    try {
      const exclude_paths = computeExcludePaths(treeData.root, checkedMap);
      await knowledgeAPI.setRepoIndexScope(repoId, exclude_paths);
      toast.success('Seleccion de indexado guardada');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const stats = useMemo(() => {
    if (!treeData) return null;
    return {
      total: treeData.total_files,
      indexable: treeData.total_indexable,
      truncated: treeData.truncated,
    };
  }, [treeData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">Contenido a indexar — {repoId}</DialogTitle>
          <DialogDescription className="text-xs">
            Por defecto todo esta marcado. Destilda lo que no quieras indexar, o usa
            {' '}&quot;Desmarcar todo&quot; y vuelve a marcar solo las carpetas deseadas.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cargando arbol...
          </div>
        ) : !treeData?.cloned ? (
          <p className="text-sm text-amber-700 py-4">{treeData?.message || 'Descarga el repo primero para ver el arbol.'}</p>
        ) : (
          <>
            <div className="flex items-center justify-between text-[10px] text-zinc-500 pb-1">
              <span>
                {stats?.total ?? 0} archivos
                {stats?.indexable != null ? ` · ${stats.indexable} indexables` : ''}
                {stats?.truncated ? ' · arbol truncado' : ''}
              </span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={handleDeselectAll}>
                  Desmarcar todo
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={handleSelectAll}>
                  Marcar todo
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto border rounded-sm p-2 min-h-[240px] max-h-[50vh]">
              <TreeNode
                node={treeData.root}
                depth={0}
                checkedMap={checkedMap}
                expanded={expanded}
                onToggleExpand={handleToggleExpand}
                onToggleCheck={handleToggleCheck}
              />
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => handleSave()}
            disabled={saving || loading || !treeData?.cloned}
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Guardar seleccion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
